import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Label,
  Card,
  CardContent,
} from '@roger/ui-components';
import {
  getOrderAllowedVisits,
  updateOrderData,
  client,
} from '../../services/api';
import type {
  OrderResponse,
  VisitResponse,
} from '../../services/api/types.gen';

const DEFAULT_HOMEBOUND_STATEMENT =
  'The patient is homebound because they are unable to leave home without considerable and taxing effort. The patient requires the assistance of another person or the use of supportive devices to leave the home.';

interface CMS485OrderFormProps {
  visit: OrderResponse;
  onSave?: () => void;
}

const schema = z.object({
  linked_oasis_visit_id: z.string().min(1, 'OASIS visit selection is required'),
  dme_supplies: z.string().optional(),
  risk_profile: z.string().optional(),
  orders_and_treatments: z.string().optional(),
  homebound_certification_statement: z.string().optional(),
  safety_measures: z.string().optional(),
  nutritional_requirements: z.string().optional(),
  goals: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function visitToFormValues(visit: OrderResponse): FormValues {
  const data = (visit.order_data ?? {}) as Record<string, unknown>;
  return {
    linked_oasis_visit_id: visit.linked_oasis_visit_id ?? '',
    dme_supplies: (data.dme_supplies as string) ?? '',
    risk_profile: (data.risk_profile as string) ?? '',
    orders_and_treatments: (data.orders_and_treatments as string) ?? '',
    homebound_certification_statement:
      (data.homebound_certification_statement as string) ?? '',
    safety_measures: (data.safety_measures as string) ?? '',
    nutritional_requirements: (data.nutritional_requirements as string) ?? '',
    goals: (data.goals as string) ?? '',
  };
}

export const CMS485OrderForm: React.FC<CMS485OrderFormProps> = ({ visit }) => {
  const [oasisVisits, setOasisVisits] = useState<VisitResponse[]>([]);
  const [loadingOasisVisits, setLoadingOasisVisits] = useState(false);
  const [useStandardHomebound, setUseStandardHomebound] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: visitToFormValues(visit),
  });

  // Fetch eligible OASIS visits
  useEffect(() => {
    const fetchOasisVisits = async () => {
      setLoadingOasisVisits(true);
      try {
        const res = await getOrderAllowedVisits({
          path: { order_id: visit.id },
          client,
        });
        if (res.data) {
          setOasisVisits(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch OASIS visits:', err);
      } finally {
        setLoadingOasisVisits(false);
      }
    };
    fetchOasisVisits();
  }, [visit.episode_id]);

  // Standard homebound template toggle
  const handleStandardTemplateChange = (checked: boolean) => {
    setUseStandardHomebound(checked);
    if (checked) {
      setValue(
        'homebound_certification_statement',
        DEFAULT_HOMEBOUND_STATEMENT
      );
    }
  };

  // Debounced auto-save using watch subscription (skip initial mount)
  useEffect(() => {
    const subscription = watch(formValues => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(async () => {
        const { linked_oasis_visit_id, ...rest } = formValues;

        try {
          await updateOrderData({
            client,
            path: { order_id: visit.id },
            body: {
              order_data: {
                ...rest,
                linked_oasis_visit_id,
              },
              order_data_last_updated: visit.order_data_last_updated ?? null,
            },
          });
        } catch (err) {
          console.error('Error auto-saving CMS 485 form:', err);
        }
      }, 1500);
    });

    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [watch, visit.id, visit.order_data_last_updated]);

  return (
    <Card>
      <CardContent className='space-y-4'>
        {/* OASIS Visit Selector */}
        <div className='space-y-1.5'>
          <Label>OASIS Visit</Label>
          <Controller
            name='linked_oasis_visit_id'
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger selectSize='sm'>
                  <SelectValue
                    placeholder={
                      loadingOasisVisits ? 'Loading...' : 'Select OASIS visit'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {oasisVisits.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.visit_type_display_name} — {v.visit_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.linked_oasis_visit_id && (
            <p className='text-xs text-red-500'>
              {errors.linked_oasis_visit_id.message}
            </p>
          )}
        </div>

        {/* DME & Supplies */}
        <div className='space-y-1.5'>
          <Label>DME & Supplies</Label>
          <Textarea
            {...register('dme_supplies')}
            placeholder='Enter DME and supplies'
            textareaSize='sm'
          />
        </div>

        {/* Risk Profile */}
        <div className='space-y-1.5'>
          <Label>Patient Risk Profile</Label>
          <Textarea
            {...register('risk_profile')}
            placeholder='Enter risk profile'
            textareaSize='sm'
          />
        </div>

        {/* Orders & Treatments */}
        <div className='space-y-1.5'>
          <Label>Orders & Treatments</Label>
          <Textarea
            {...register('orders_and_treatments')}
            placeholder='Enter orders and treatments'
            textareaSize='sm'
          />
        </div>

        {/* Homebound Certification Statement */}
        <div className='space-y-1.5'>
          <Label>Homebound Certification Statement</Label>
          <div className='flex items-center gap-2 mb-1'>
            <Checkbox
              checked={useStandardHomebound}
              onCheckedChange={checked =>
                handleStandardTemplateChange(checked === true)
              }
            />
            <span className='text-xs text-muted-foreground'>
              Use standard template
            </span>
          </div>
          <Textarea
            {...register('homebound_certification_statement')}
            placeholder='Enter homebound certification statement'
            textareaSize='sm'
          />
        </div>

        {/* Safety Measures */}
        <div className='space-y-1.5'>
          <Label>Safety Measures</Label>
          <Textarea
            {...register('safety_measures')}
            placeholder='Enter safety measures'
            textareaSize='sm'
          />
        </div>

        {/* Nutritional Requirements */}
        <div className='space-y-1.5'>
          <Label>Nutritional Requirements</Label>
          <Textarea
            {...register('nutritional_requirements')}
            placeholder='Enter nutritional requirements'
            textareaSize='sm'
          />
        </div>

        {/* Goals */}
        <div className='space-y-1.5'>
          <Label>Goals & Measurable Outcomes</Label>
          <Textarea
            {...register('goals')}
            placeholder='Enter goals and measurable outcomes'
            textareaSize='sm'
          />
        </div>
      </CardContent>
    </Card>
  );
};
